import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'

import { getCurrentFeatureFlags } from 'api/auth'
import { FlagFormat } from 'api/auth/getCurrentFeatureFlags'
import useUser from 'hooks/useUser'

enum FeatureFlags {
    customerEligibilityEnabledForAutoApps = 'SHOCK-67-enable-state-eligibility-for-automobile-applications',
    dealTermsTable = 'ABBA-1974-show_new_terms_table_in_work_the_deal_for_powersport_and_rv',
    dealTermsTableExtendedLogging = 'ABBA-3112-enable-deal-terms-table-extended-logging',
    dealTermsTableFiltering = 'BLUR-459-add-filters-to-available-deal-terms',
    dealershipLenderAgreementValidation = 'FIKA-3041-dealership-lender-agreement-submission-validation',
    decisionPage2Powersports = 'DP-136-decision-page-2-0-for-powersports',
    documentViewerForStips = 'SHOCK-247-enable-document-viewer-for-stips-on-dealer-portal',
    flatCancel = 'ZEN-619-flat-cancel-button-feature-flags',
    invoicePriceForNewRv = 'RV-263-invoice-price-for-new-rv',
    leadsPage = 'ROGUE-270-enable-leads-page-for-dealer-portal',
    newRvApp = 'BLUR-777-enable-new-rv-application',
    newWTDApplicationOverview = 'ABBA-3636-wtd-modernization-application-header',
    newWTDItemizationSection = 'ABBA-3651-wtd-modernization-itemization-section',
    newWTDNotificationDrawer = 'ABBA-3786-wtd-dealer-messages-and-checklist',
    newWTDStickyApplicationNavigation = 'ABBA-3610-wtd-modernization-application-sticky-navigation-bar',
    newWtdPath = 'ABBA-1454-determine-wtd-path',
    newWtdStips = 'FIKA-3873-wtd-modernization-stips',
    oemRebatePowerSports = 'ABBA-3007-enable-oem-rebate',
    returnCustomerPsApp2 = 'FIKA-2643-return-customer-ps-app-2-0',
    rideNowWhiteLabelRedirect = 'ABBA-3328-redirect-ride-now-dealers-to-whitelabel',
    routePsAppToNewReadOnlyPage = 'FIKA-3222-dealer-ps-app-read-only-page',
    salesPersonFlexLink = 'DITTO-943-enable-salesperson-flex-link-dealer-portal',
    selfServiceManagement = 'CSP-1740-add-self-service-management-feature-flag',
    shortApp = 'SWED-8251-add-shortened-application-feature-flag',
    showFlexLink = 'DITTO-690-show-flex-link-homepage',
    showNewSafeCheckPage = 'ABBA-2032-new-safecheck-report-page',
    showRePowerTile = 'BLUR-658-show-repower-tile',
    singleBucket = 'FIKA-3637-single-bucket',
    sweepstakesTheme = 'FIKA-3055-sweepstakes-theme-for-dealer-portal',
    tradeInAdjustment = 'LP-16-enable-trade-in-adjustment',
    useNewApplicationStatusPage = 'LP-62-use-new-application-status-page',
    webAppLoyaltyDashboard = 'CSP-1875-migrate-loyalty-dashboard-from-old-to-new-dealer-portal',
    whiteLabelRedirect = 'SHOCK-796-redirect-dealers-to-white-labelled-dealer-portal',
    newWTDCreditScoreDisclosureSection = 'FIKA-3938-wtd-modernization-credit-score-section',
}

type FeatureFlagsMap = {
    [K in keyof typeof FeatureFlags]?: boolean | undefined
}

export type FeatureFlagContextInterface = FeatureFlagsMap & {
    handleShouldUpdateFlags?: () => void
}

export const FeatureFlagContext =
    createContext<FeatureFlagContextInterface | null>(null)

interface FeatureFlagContextProviderProps {
    readonly children: React.ReactNode
}

const setFlagValues = (flags: FlagFormat): FeatureFlagsMap =>
    Object.entries(FeatureFlags)
        .map(([key, flagName]) => {
            const flagValue =
                flags.flags?.[flagName] || flags.switches?.[flagName]

            const isActive = flagValue?.is_active ?? false

            return { [key]: isActive }
        })
        .reduce((acc, curr) => ({ ...acc, ...curr }), {})

function FeatureFlagContextProvider({
    children,
}: FeatureFlagContextProviderProps) {
    const { isLoggedIn } = useUser()

    // all flags will have an initial value of `false`
    const [flags, setFlags] = useState(
        setFlagValues({
            flags: {},
            switches: {},
        } as FlagFormat),
    )

    const [shouldUpdateFlags, setShouldUpdateFlags] = useState(true as boolean)

    const cachedFlags = useMemo(
        () => async () => {
            getCurrentFeatureFlags().then((response: FlagFormat) => {
                setFlags(setFlagValues(response))

                setShouldUpdateFlags(false)
            })
        },
        [],
    )

    useEffect(() => {
        if (shouldUpdateFlags) {
            cachedFlags()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [window.location.hash])

    useEffect(() => {
        cachedFlags()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn])

    const ctx = React.useMemo(
        () => ({
            ...flags,
            handleShouldUpdateFlags: () => {
                setShouldUpdateFlags(true)
            },
        }),
        [flags],
    )

    return (
        <FeatureFlagContext.Provider value={ctx}>
            {children}
        </FeatureFlagContext.Provider>
    )
}

export default FeatureFlagContextProvider

export const useFeatureFlagContext = () => useContext(FeatureFlagContext)
